import { Close } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { useField } from 'formik';
import { CSSProperties, FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import copy from 'copy-to-clipboard';

import { GameResult, WinScreenSettings, LooseScreenSettings } from '../../types';
import { GenericField } from './form';
import { FieldType, Text } from '../../types/form-fields';
import { ApiError, TApiError } from '../../components';
import { useAppSelector } from '../../lib';

type ResultScreenProps = {
  visible: boolean;
  gameResult?: GameResult;
  gameResultError?: TApiError;
  onClose: () => void;
};

export const ResultScreen: FC<ResultScreenProps> = ({ visible, gameResult, gameResultError, onClose }) => {
  const [winScreen] = useField<WinScreenSettings>(`campaign.winScreen`);
  const [looseScreen] = useField<LooseScreenSettings>(`campaign.looseScreen`);
  const [transitionEnded, setTransitionEnded] = useState(false);
  const shouldReduceFieldsSize = useAppSelector((state) => state.config.reduceUISize);

  const getDisplayValue = (value: string): string => {
    if (!gameResult || !gameResult.win || !gameResult.coupon) {
      return value;
    }

    const {
      coupon: { name, code: couponValue, description },
    } = gameResult;

    return value
      .replace(/%name%/g, name || '')
      .replace(/%code%/g, couponValue || '')
      .replace(/%description%/g, description || '');
  };

  const copyCode = useCallback(() => {
    if (gameResult?.coupon?.code) {
      copy(gameResult.coupon.code);
    }
  }, [gameResult?.coupon?.code]);

  const handleTransitionEnd = useCallback(() => {
    if (visible) {
      setTransitionEnded(true);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setTransitionEnded(false);
    }
  }, [visible]);

  const getGameResultContent = (gameResult: GameResult) => {
    const sliceName = gameResult.win ? 'campaign.winScreen.blocks' : 'campaign.looseScreen.blocks';
    const blocks = gameResult.win ? winScreen.value.blocks : looseScreen.value.blocks;
    const isPrizeIsLink = gameResult.coupon?.type === 'link';
    const firstTextBlock = blocks.find((block) => block.type === FieldType.Text) as unknown as Text | undefined;
    const shouldRenderLink = isPrizeIsLink && gameResult.coupon?.link && gameResult.coupon?.linkText;

    return (
      <>
        {blocks.map((formField, index) => {
          const isButton = formField.type === FieldType.Button;

          if (isButton && isPrizeIsLink) {
            return null;
          }

          return (
            <GenericField
              key={index}
              name={`${sliceName}[${index}]`}
              formField={formField}
              getDisplayValue={formField.type === 'text' ? getDisplayValue : undefined}
              onButtonClick={isButton ? copyCode : undefined}
              reduceSize={shouldReduceFieldsSize}
            />
          );
        })}

        {shouldRenderLink && (
          <PrizeLink
            target="_blank"
            rel="noopener noreferrer"
            color={firstTextBlock?.color || '#fff'}
            href={gameResult.coupon?.link}
          >
            {gameResult.coupon?.linkText}
          </PrizeLink>
        )}
      </>
    );
  };

  const getScreenStyles = (): CSSProperties => {
    return {
      backgroundColor: winScreen.value.bgColor,
      color: winScreen.value.closeButtonColor,
    };
  };

  const renderContent = () => {
    if (!transitionEnded) {
      return <CircularProgress color="inherit" />;
    }

    if (gameResult) {
      return <Content>{getGameResultContent(gameResult)}</Content>;
    }

    if (gameResultError) {
      return (
        <ApiError error={gameResultError} color={winScreen.value.closeButtonColor} fontSize={16} textAlign="center" />
      );
    }

    return <CircularProgress color="inherit" />;
  };

  return (
    <ResultScreenWrapper visible={visible} style={getScreenStyles()} onTransitionEnd={handleTransitionEnd}>
      <IconButton className="eg-close-result-button" onClick={onClose} size="small" aria-label="close" title="close">
        <Close />
      </IconButton>

      {renderContent()}
    </ResultScreenWrapper>
  );
};

export const ResultScreenWrapper = styled.div<{ visible: boolean }>`
  position: absolute;
  inset: 0 0 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #000;
  color: #fff;
  transition: all 0.3s ease;
  padding: 12px;
  z-index: -1;
  opacity: 0;
  transform: scale(0.7);

  ${(p) =>
    p.visible &&
    `
    z-index: 1000;
    opacity: 1;
    transform: scale(1);
  `};

  @media screen and (min-width: 768px) {
    padding: 24px;
  }

  .eg-close-result-button {
    position: absolute;
    top: 8px;
    right: 8px;
    color: inherit;
  }

  .generic-field-text-content {
    text-align: center;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

const PrizeLink = styled.a<{ color: string }>`
  text-align: center;
  color: ${(p) => p.color};

  :visited,
  :hover,
  :active {
    color: ${(p) => p.color};
    text-decoration: underline;
    cursor: pointer;
  }
`;
